import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Separator } from '../../components/ui/separator';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Calculator,
  Download,
  FileSpreadsheet,
  TrendingUp,
  TrendingDown,
  Euro,
  Calendar,
  FileText,
  CheckCircle2,
  ArrowRight,
  ArrowDown,
  ArrowLeft,
  Building2,
  Receipt,
  Loader2,
  Info,
  Sparkles,
  BookOpen
} from 'lucide-react';
import { accountingAPI } from '../../services/api';
import { toast } from 'sonner';

const AccountingPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [period, setPeriod] = useState('month');
  
  // Export params
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [includePurchases, setIncludePurchases] = useState(true);

  useEffect(() => {
    // Set default dates (current month)
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
    
    loadStats();
  }, []);

  useEffect(() => {
    loadStats();
  }, [period]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const res = await accountingAPI.getStats(period);
      setStats(res.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const handleExportFEC = async () => {
    if (!startDate || !endDate) {
      toast.error('Veuillez sélectionner une période');
      return;
    }

    setExporting(true);
    try {
      const response = await accountingAPI.exportFEC(startDate, endDate, includePurchases);
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `FEC_${startDate}_${endDate}.txt`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Fichier FEC téléchargé !');
    } catch (error) {
      console.error('Export failed:', error);
      if (error.response?.status === 404) {
        toast.error('Aucune écriture pour cette période');
      } else {
        toast.error('Erreur lors de l\'export');
      }
    } finally {
      setExporting(false);
    }
  };

  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return '0,00 €';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const setQuickPeriod = (periodType) => {
    const now = new Date();
    let start, end;

    switch (periodType) {
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        start = new Date(now.getFullYear(), quarter * 3, 1);
        end = new Date(now.getFullYear(), quarter * 3 + 3, 0);
        break;
      case 'year':
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), 11, 31);
        break;
      default:
        return;
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-6xl mx-auto">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/dashboard/smartfacture')}
        className="text-slate-600 hover:text-slate-900 mb-2"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Retour
      </Button>

      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-600 p-6 md:p-8 text-white">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yIDItNCAyLTRzLTItMi00LTJoLTR2MTBoNGMyIDAgNC0yIDQtNHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30"></div>
        
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-white/20 backdrop-blur-sm">
              <Calculator className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">
                Comptabilité & Export CSV
              </h1>
              <p className="text-purple-100 mt-1 text-sm md:text-base">
                Écritures comptables automatiques — Export prêt pour votre comptable
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-blue-900">Qu'est-ce que le FEC ?</p>
              <p className="text-sm text-blue-700 mt-1">
                Le <strong>Fichier des Écritures Comptables</strong> est un format légal français. 
                Votre expert-comptable peut l'importer directement dans son logiciel — <strong>zéro ressaisie</strong> !
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Ventes */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="p-2 rounded-lg bg-emerald-100">
                  <TrendingUp className="h-5 w-5 text-emerald-600" />
                </div>
                Ventes (Chiffre d'affaires)
              </CardTitle>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Ce mois</SelectItem>
                  <SelectItem value="quarter">Ce trimestre</SelectItem>
                  <SelectItem value="year">Cette année</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {stats && <CardDescription>{stats.period}</CardDescription>}
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
              </div>
            ) : stats ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <p className="text-sm text-slate-500">Total HT</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {formatAmount(stats.total_sales_ht)}
                    </p>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-xl">
                    <p className="text-sm text-emerald-600">Total TTC</p>
                    <p className="text-2xl font-bold text-emerald-700">
                      {formatAmount(stats.total_sales_ttc)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                  <span className="text-sm text-amber-700">TVA collectée</span>
                  <span className="font-bold text-amber-700">
                    {formatAmount(stats.total_vat_collected)}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <FileText className="h-4 w-4" />
                  <span>{stats.invoices_count} facture(s) émise(s)</span>
                </div>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-4">Aucune donnée</p>
            )}
          </CardContent>
        </Card>

        {/* Achats */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className="p-2 rounded-lg bg-orange-100">
                <TrendingDown className="h-5 w-5 text-orange-600" />
              </div>
              Achats (Charges)
            </CardTitle>
            {stats && <CardDescription>{stats.period}</CardDescription>}
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-orange-600" />
              </div>
            ) : stats ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <p className="text-sm text-slate-500">Total HT</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {formatAmount(stats.total_purchases_ht)}
                    </p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-xl">
                    <p className="text-sm text-orange-600">Total TTC</p>
                    <p className="text-2xl font-bold text-orange-700">
                      {formatAmount(stats.total_purchases_ttc)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm text-blue-700">TVA déductible</span>
                  <span className="font-bold text-blue-700">
                    {formatAmount(stats.total_vat_deductible)}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Receipt className="h-4 w-4" />
                  <span>{stats.inbox_items_count} document(s) traité(s)</span>
                </div>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-4">Aucune donnée</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* TVA Summary */}
      {stats && (stats.total_vat_collected > 0 || stats.total_vat_deductible > 0) && (
        <Card className="border-2 border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-violet-100">
                  <Euro className="h-6 w-6 text-violet-600" />
                </div>
                <div>
                  <p className="font-semibold text-violet-900">Solde TVA estimé</p>
                  <p className="text-sm text-violet-600">TVA collectée - TVA déductible</p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-3xl font-bold ${
                  (stats.total_vat_collected - stats.total_vat_deductible) >= 0 
                    ? 'text-violet-700' 
                    : 'text-emerald-700'
                }`}>
                  {formatAmount(stats.total_vat_collected - stats.total_vat_deductible)}
                </p>
                <p className="text-sm text-violet-600">
                  {(stats.total_vat_collected - stats.total_vat_deductible) >= 0 
                    ? 'À reverser' 
                    : 'Crédit de TVA'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export comptable Section */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-blue-600" />
            Export comptable CSV
          </CardTitle>
          <CardDescription>
            Générez le fichier des écritures comptables pour votre expert-comptable
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Quick periods */}
          <div className="space-y-2">
            <Label>Période rapide</Label>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => setQuickPeriod('month')}>
                Ce mois
              </Button>
              <Button variant="outline" size="sm" onClick={() => setQuickPeriod('lastMonth')}>
                Mois dernier
              </Button>
              <Button variant="outline" size="sm" onClick={() => setQuickPeriod('quarter')}>
                Ce trimestre
              </Button>
              <Button variant="outline" size="sm" onClick={() => setQuickPeriod('year')}>
                Cette année
              </Button>
            </div>
          </div>

          <Separator />

          {/* Custom dates */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start-date">Date de début</Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end-date">Date de fin</Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          {/* Options */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="space-y-0.5">
              <Label htmlFor="include-purchases">Inclure les achats (Inbox)</Label>
              <p className="text-sm text-slate-500">
                Documents importés dans votre Inbox SmartFacture
              </p>
            </div>
            <Switch
              id="include-purchases"
              checked={includePurchases}
              onCheckedChange={setIncludePurchases}
            />
          </div>

          {/* Export Button */}
          <Button
            onClick={handleExportFEC}
            disabled={exporting || !startDate || !endDate}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 h-12 text-lg"
            data-testid="export-fec-btn"
          >
            {exporting ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Export en cours...
              </>
            ) : (
              <>
                <Download className="h-5 w-5 mr-2" />
                Télécharger le fichier FEC
              </>
            )}
          </Button>

          {/* Instructions */}
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div className="space-y-2">
                <p className="font-medium text-emerald-900">Comment utiliser le fichier FEC ?</p>
                <ol className="text-sm text-emerald-700 space-y-1 list-decimal list-inside">
                  <li>Téléchargez le fichier FEC (.txt)</li>
                  <li>Envoyez-le à votre expert-comptable</li>
                  <li>Il l'importe dans son logiciel (Sage, Cegid, Quadra...)</li>
                  <li>Zéro ressaisie, zéro erreur !</li>
                </ol>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Coming Soon - Catégorisation IA */}
      <Card className="border-dashed border-2 border-slate-300 bg-slate-50/50">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100">
              <Sparkles className="h-6 w-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-slate-900">Catégorisation IA</p>
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  Bientôt
                </Badge>
              </div>
              <p className="text-sm text-slate-600 mt-1">
                L'IA suggérera automatiquement les comptes comptables pour vos achats 
                (ex: "Restaurant" → 6256 Missions)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountingPage;
